import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { MonthYearSelector } from '@travellegacy/nexops-design-system';
import {
  SortableTable,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { salesAPI } from '@/shared/lib/api';
import { formatCurrency, formatDate } from '@/shared/lib/utils';
import { PeriodToggle, YearSelect } from '../components/AnnualBarChart';
import { SalesAnnualView } from '../components/SalesAnnualView';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';

interface Sale {
  id: string;
  paymentId: string;
  clientName: string;
  clientPhone: string;
  amount: number;
  netAmount: number;
  feeAmount: number;
  paymentMethod: string | null;
  installments: number;
  status: string;
  saleType: 'NEW_CLIENT' | 'RENEWAL' | 'CONSULTORIA' | 'GEP';
  metadata: unknown;
  createdAt: string;
  updatedAt: string;
  // clientEmail removido da listagem (disponível apenas no detalhamento)
}


export default function SalesListPage() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [typeFilter, setTypeFilter] = useState<'all' | 'NEW_CLIENT' | 'RENEWAL' | 'CONSULTORIA' | 'GEP'>('all');
  const [searchEmail, setSearchEmail] = useState('');
  const [debouncedSearchEmail, setDebouncedSearchEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchEmail(searchEmail);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchEmail]);

  // Query para listar vendas (com filtro)
  const { data: salesData, isLoading: isLoadingSales } = useQuery({
    queryKey: ['sales', currentPage, typeFilter, selectedMonth, selectedYear, debouncedSearchEmail],
    queryFn: async () => {
      const response = await salesAPI.list({
        page: currentPage,
        limit: pageSize,
        saleType: typeFilter === 'all' ? undefined : typeFilter,
        month: selectedMonth,
        year: selectedYear,
        clientEmail: debouncedSearchEmail || undefined,
      });
      return response.data;
    },
  });

  // Query para buscar todas as vendas do mês sem filtro de tipo (para contar os tipos nas tabs)
  const { data: allSalesData } = useQuery({
    queryKey: ['sales-all', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await salesAPI.list({
        page: 1,
        limit: 100, // Máximo permitido pelo backend
        month: selectedMonth,
        year: selectedYear,
      });
      return response.data;
    },
  });

  // Query para buscar vendas filtradas pelo tipo atual (para calcular o total correto)
  const { data: filteredSalesData } = useQuery({
    queryKey: ['sales-filtered-total', selectedMonth, selectedYear, typeFilter],
    queryFn: async () => {
      if (typeFilter === 'all') {
        // Para 'all', precisamos buscar todas as páginas ou usar um limite alto
        // Vamos buscar página por página até ter todas
        let allSales: Sale[] = [];
        let currentPage = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await salesAPI.list({
            page: currentPage,
            limit: 100,
            month: selectedMonth,
            year: selectedYear,
          });
          allSales = [...allSales, ...(response.data.data as Sale[])];
          hasMore = currentPage < (response.data.pagination?.totalPages || 0);
          currentPage++;
        }

        return allSales;
      } else {
        // Para tipos específicos, buscar todas as páginas daquele tipo
        let allSales: Sale[] = [];
        let currentPage = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await salesAPI.list({
            page: currentPage,
            limit: 100,
            saleType: typeFilter,
            month: selectedMonth,
            year: selectedYear,
          });
          allSales = [...allSales, ...(response.data.data as Sale[])];
          hasMore = currentPage < (response.data.pagination?.totalPages || 0);
          currentPage++;
        }

        return allSales;
      }
    },
  });

  const sales = (salesData?.data || []) as Sale[];
  const allSalesTotal = allSalesData?.pagination?.total || 0;
  const pagination = salesData?.pagination || { page: 1, limit: pageSize, total: 0, totalPages: 0 };

  // Calcular contagens para as tabs
  // Usamos os allSales retornados + fazemos queries adicionais para cada tipo
  const { data: newClientCount } = useQuery({
    queryKey: ['sales-count-NEW_CLIENT', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await salesAPI.list({
        page: 1,
        limit: 1,
        saleType: 'NEW_CLIENT',
        month: selectedMonth,
        year: selectedYear,
      });
      return response.data.pagination?.total || 0;
    },
  });

  const { data: renewalCount } = useQuery({
    queryKey: ['sales-count-RENEWAL', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await salesAPI.list({
        page: 1,
        limit: 1,
        saleType: 'RENEWAL',
        month: selectedMonth,
        year: selectedYear,
      });
      return response.data.pagination?.total || 0;
    },
  });

  const { data: consultoriaCount } = useQuery({
    queryKey: ['sales-count-CONSULTORIA', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await salesAPI.list({
        page: 1,
        limit: 1,
        saleType: 'CONSULTORIA',
        month: selectedMonth,
        year: selectedYear,
      });
      return response.data.pagination?.total || 0;
    },
  });

  const { data: gepCount } = useQuery({
    queryKey: ['sales-count-GEP', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await salesAPI.list({
        page: 1,
        limit: 1,
        saleType: 'GEP',
        month: selectedMonth,
        year: selectedYear,
      });
      return response.data.pagination?.total || 0;
    },
  });

  const typeCounts = {
    all: allSalesTotal,
    NEW_CLIENT: newClientCount || 0,
    RENEWAL: renewalCount || 0,
    CONSULTORIA: consultoriaCount || 0,
    GEP: gepCount || 0,
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'success' | 'error' | 'warning' | 'info' | 'outline' => {
    if (status === 'approved') return 'success';
    if (status === 'pending' || status === 'in_process') return 'warning';
    if (status === 'rejected' || status === 'cancelled') return 'error';
    return 'default';
  };

  const getStatusLabel = (status: string) => {
    if (status === 'approved') return 'Aprovado';
    if (status === 'pending') return 'Pendente';
    if (status === 'in_process') return 'Em Processamento';
    if (status === 'rejected') return 'Rejeitado';
    if (status === 'cancelled') return 'Cancelado';
    return status;
  };

  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return 'N/A';
    if (method === 'pix') return 'PIX';
    if (method === 'credit_card') return 'Cartão';
    if (method === 'debit_card') return 'Cartão de Débito';
    if (method === 'boleto') return 'Boleto';
    return method;
  };

  const getSaleTypeLabel = (saleType: 'NEW_CLIENT' | 'RENEWAL' | 'CONSULTORIA' | 'GEP') => {
    if (saleType === 'NEW_CLIENT') return 'Novo Cliente';
    if (saleType === 'RENEWAL') return 'Renovação';
    if (saleType === 'CONSULTORIA') return 'Consultoria';
    if (saleType === 'GEP') return 'GEP';
    return saleType;
  };

  const getSaleTypeBadgeVariant = (saleType: 'NEW_CLIENT' | 'RENEWAL' | 'CONSULTORIA' | 'GEP'): 'default' | 'success' | 'error' | 'warning' | 'info' | 'outline' => {
    if (saleType === 'NEW_CLIENT') return 'info';
    if (saleType === 'RENEWAL') return 'success';
    if (saleType === 'CONSULTORIA') return 'warning';
    if (saleType === 'GEP') return 'default';
    return 'default';
  };

  // Calcular total de vendas do mês (apenas status aprovado)
  // Usa filteredSalesData que busca todas as páginas do tipo filtrado
  const filteredSales = (filteredSalesData || []) as Sale[];
  const totalSales = filteredSales
    .filter(sale => sale.status === 'approved')
    .reduce((sum, sale) => sum + sale.netAmount, 0);

  // Nome do mês
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  const monthName = monthNames[selectedMonth - 1];

  // Cabeçalho com alternância Mensal | Anual
  const header = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-3xl font-bold">Receitas</h1>
        <p className="text-muted-foreground mt-1">
          {viewMode === 'year' ? 'Receitas do ano, mês a mês, por tipo de venda' : 'Visualize todas as receitas com filtros e detalhes'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <PeriodToggle value={viewMode} onChange={setViewMode} />
        {viewMode === 'year' ? (
          <YearSelect value={selectedYear} onChange={setSelectedYear} />
        ) : (
          <MonthYearSelector
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
          />
        )}
      </div>
    </div>
  );

  // Visão anual: clicar num mês abre a visão mensal dele
  if (viewMode === 'year') {
    return (
      <div className="space-y-6">
        {header}
        <SalesAnnualView
          year={selectedYear}
          onSelectMonth={(month) => {
            setSelectedMonth(month);
            setViewMode('month');
          }}
        />
      </div>
    );
  }

  if (isLoadingSales) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

        {/* Filters and Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Total de Vendas do Mês */}
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-foreground">{monthName}:</span>
                  <p className="text-2xl font-bold text-primary">{formatCurrency(totalSales)}</p>
                </div>
              </div>

              {/* Type Tabs */}
              <TabFilter
                tabs={[
                  { value: 'all', label: 'Todas', count: typeCounts.all },
                  { value: 'NEW_CLIENT', label: 'Novos Clientes', count: typeCounts.NEW_CLIENT },
                  { value: 'RENEWAL', label: 'Renovações', count: typeCounts.RENEWAL },
                  { value: 'CONSULTORIA', label: 'Consultoria', count: typeCounts.CONSULTORIA },
                  { value: 'GEP', label: 'GEP', count: typeCounts.GEP },
                ]}
                currentTab={typeFilter}
                onChange={(value) => {
                  setTypeFilter(value as typeof typeFilter);
                  setCurrentPage(1);
                }}
              />

              {/* Search */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por email do cliente..."
                    value={searchEmail}
                    onChange={(e) => {
                      setSearchEmail(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                {sales.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    Nenhuma venda encontrada
                  </div>
                ) : (
                  <SortableTable
                    data={sales}
                    defaultSortKey="createdAt"
                    defaultSortDirection="desc"
                  >
                    {(sortedSales) => (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead sortable sortKey="saleType">Tipo</TableHead>
                            <TableHead sortable sortKey="createdAt">Data</TableHead>
                            <TableHead sortable sortKey="clientName">Cliente</TableHead>
                            <TableHead sortable sortKey="paymentMethod">Método</TableHead>
                            <TableHead sortable sortKey="installments">Parcelas</TableHead>
                            <TableHead sortable sortKey="amount" align="right">Valor Bruto</TableHead>
                            <TableHead sortable sortKey="feeAmount" align="right">Taxas</TableHead>
                            <TableHead sortable sortKey="netAmount" align="right">Valor Líquido</TableHead>
                            <TableHead sortable sortKey="status">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedSales.map((sale) => (
                            <TableRow key={sale.id}>
                              <TableCell>
                                <Badge variant={getSaleTypeBadgeVariant(sale.saleType)}>
                                  {getSaleTypeLabel(sale.saleType)}
                                </Badge>
                              </TableCell>
                              <TableCell>{formatDate(sale.createdAt)}</TableCell>
                              <TableCell className="font-medium">{sale.clientName}</TableCell>
                              <TableCell>{getPaymentMethodLabel(sale.paymentMethod)}</TableCell>
                              <TableCell>{sale.installments}x</TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(sale.amount)}
                              </TableCell>
                              <TableCell className="text-right text-orange-600 dark:text-orange-400">
                                {formatCurrency(sale.feeAmount)}
                              </TableCell>
                              <TableCell className="text-right font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(sale.netAmount)}
                              </TableCell>
                              <TableCell>
                                <Badge variant={getStatusBadgeVariant(sale.status)}>
                                  {getStatusLabel(sale.status)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </SortableTable>
                )}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Página {pagination.page} de {pagination.totalPages} ({pagination.total} vendas)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={currentPage === pagination.totalPages}
                    >
                      Próxima
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
    </div>
  );
}
